/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { FC, useMemo, useState, useEffect, useRef } from "react";

// eslint-disable-next-line node/no-missing-import
import type * as CSS from "csstype";

import { WidthProvider, Responsive as ResponsiveGridLayout } from "react-grid-layout";

// My Components:
// import { DivE } from ".";

// Redux-hooks:
// import { useAppSelector } from "../../libs/redux-hooks.ts";
// Reducers:
// import { getTheme } from "../../libs/reducer.ts";

export type screenSizesType = {
  lg?:number, md?:number, sm?:number, xs?:number, xxs?:number
};

export type LayoutItem = {
  i:string, x:number, y:number, w:number, h:number,
  minW?:number, maxW?:number, minH?:number, maxH?:number,
  static?:boolean, isDraggable?:boolean, isResizable?:boolean, isBounded?:boolean,
  resizeHandles?:Array<"s" | "w" | "e" | "n" | "sw" | "nw" | "se" | "ne">
};

export type LayoutType = {
  lg:LayoutItem[], md?:LayoutItem[], sm?:LayoutItem[], xs?:LayoutItem[], xxs?:LayoutItem[]
};

// eslint-disable-next-line no-use-before-define
type ItemCallback = (layout:gridLayoutProps, oldItem:LayoutItem, newItem:LayoutItem, placeholder:LayoutItem, event:MouseEvent, element:HTMLElement) => void;

export type gridLayoutProps = { // As example (available the maximum props), but not used in this component
  width?:number, autoSize?:boolean, cols?:number,
  rowHeight?:number,
  verticalCompact?:boolean, compactType?:"horizontal" | "vertical" | null,
  draggableCancel?:string, draggableHandle?:string,
  layout?:Array<LayoutItem>,
  margin?:[number, number],
  containerPadding?:[number, number],
  droppingItem?:{i:string, w:number, h:number},
  isDraggable?:boolean,
  isResizable?:boolean,
  isBounded?:boolean,
  // eslint-disable-next-line react/boolean-prop-naming
  preventCollision?:boolean,
  // eslint-disable-next-line react/boolean-prop-naming
  allowOverlap?:boolean,
  useCSSTransforms?:boolean,
  transformScale?:number,
  isDroppable?:boolean,
  resizeHandles?:Array<"s" | "w" | "e" | "n" | "sw" | "nw" | "se" | "ne">,
  resizeHandle?:React.ReactElement<any> | ((resizeHandleAxis:any) => React.ReactElement<any>), // resizeHandleAxis:ResizeHandleAxis
  onLayoutChange?:(layout:LayoutType, layouts?:Array<LayoutType>) => void,
  onDragStart?:ItemCallback,
  onDrag?:ItemCallback,
  onDragStop?:ItemCallback,
  onResizeStart?:ItemCallback,
  onResize?:ItemCallback,
  onResizeStop?:ItemCallback,
  onDrop?:(layout:gridLayoutProps, item:LayoutItem, event:Event) => void,
  innerRef?:React.Ref<"div">
};

type componentProps = { // Should be like :gridLayoutProps
  id?:string,
  layouts?:{[key:string]: Array<LayoutItem>},
  className?:string,
  style?:React.CSSProperties | CSS.Properties,
  resizeHandles?:Array<"s" | "w" | "e" | "n" | "sw" | "nw" | "se" | "ne">,
  draggableHandle?:string,
  cols?:screenSizesType,
  items?:number,
  breakpoints?:screenSizesType,
  rowHeight?:number,
  isBounded?:boolean,
  isResizable?:boolean,
  isDraggable?:boolean,
  // eslint-disable-next-line react/boolean-prop-naming
  preventCollision?:boolean,
  // eslint-disable-next-line react/boolean-prop-naming
  allowOverlap?:boolean,
  compactType?:"horizontal" | "vertical" | null,
  transformScale?:number,
  margin?:[number, number],
  containerPadding?:[number, number],
  children?:React.ReactNode,
  onLayoutChange?:(layout:LayoutType, layouts?:Array<LayoutType>) => void,
  onBreakpointChange?:(breakpoint) => void,
  onDragStart?:ItemCallback,
  onDrag?:ItemCallback,
  onDragStop?:ItemCallback,
  onResizeStart?:ItemCallback,
  onResize?:ItemCallback,
  onResizeStop?:ItemCallback,
  onDrop?:(layout:gridLayoutProps, item:LayoutItem, event:Event) => void,
};

const ComponentReactGridLayout = WidthProvider(ResponsiveGridLayout);

const ReactGridLayout:FC<componentProps> = (props) => {
  // const dispatch = useAppDispatch();
  // const theme = useAppSelector(getTheme);

  const cols = useMemo(() => (props.cols || { lg: 24, md: 20, sm: 12, xs: 8, xxs: 4 }), [props.cols]);
  const breakpoints = useMemo(() => (props.breakpoints || { lg: 1200, md: 992, sm: 768, xs: 576, xxs: 0 }), [props.breakpoints]);

  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setTimeout(() => { setMounted(true) }, 500);
  }, []);

  return (mounted && props && props.layouts ? (
    <ComponentReactGridLayout id={props.id} className={props.className || "layout"} style={props.style}
      layouts={props.layouts}
      breakpoints={breakpoints} cols={cols}
      compactType={props.compactType ?? null}
      preventCollision={props.preventCollision ?? true}
      allowOverlap={props.allowOverlap ?? false}
      margin={props.margin || [10, 10]}
      rowHeight={props.rowHeight || 60}
      draggableHandle={props.draggableHandle || ".dragMe"}
      resizeHandles={props.resizeHandles || ["se"]}

      onLayoutChange={props.onLayoutChange}
      onBreakpointChange={props.onBreakpointChange}
      onDragStart={props.onDragStart}
      onDrag={props.onDrag}
      onDragStop={props.onDragStop}
      onResizeStart={props.onResizeStart}
      onResize={props.onResize}
      onResizeStop={props.onResizeStop}
      onDrop={props.onDrop}
    >
      {props.children || []}
    </ComponentReactGridLayout>
  ) : (
    <div key="loading-desktop-apps" />
  ));
}

export default ReactGridLayout;
/* eslint-enable @typescript-eslint/no-unused-vars */
